import rawCatalog from "../../../shared/safetyqr-kit-packaging.catalog.json"

export type SafetyQrFieldStatus = "approved" | "not_applicable" | "tbd"
export type SafetyQrLaunchStatus = "blocked" | "launch_ready"
export type SafetyQrProductFamily = string
export type SafetyQrVehicleClass = string | null
export type SafetyQrSolutionCategory = string | null
export type SafetyQrKitVariant = string

export interface SafetyQrOpenDecision {
  id: string
  status: "pending_approval"
  summary: string
  title: string
}

export interface SafetyQrFieldValue<TValue> {
  status: SafetyQrFieldStatus
  value?: TValue | null
  token?: string | null
  label?: string | null
  width?: number
  height?: number
}

export interface SafetyQrTagDefinition {
  icon: SafetyQrFieldValue<never>
  identifierLine: SafetyQrFieldValue<never>
  placement: SafetyQrFieldValue<string>
  postActivationRoutePolicy: SafetyQrFieldValue<never>
  primaryCopy: SafetyQrFieldValue<string>
  printSlot: string
  printTemplateId: string
  reportTemplateId: string | null
  role: string
  sizeMm: SafetyQrFieldValue<never>
  slot: string
}

export interface SafetyQrVariantDefinition {
  activationModel: {
    activationScope: "kit"
    preferredInitiationRole: string | null
    sharedActivation: true
    siblingActivationBehavior: "activate_entire_kit"
  }
  displayName: string
  kitVariant: SafetyQrKitVariant
  launchGate: {
    blockedByDecisionIds: string[]
    launchable: boolean
    note: string
    status: SafetyQrLaunchStatus
  }
  solutionCategory: SafetyQrSolutionCategory
  preActivationRoutePolicy: "register_kit"
  printTemplateId: string
  productFamily: SafetyQrProductFamily
  reportTemplateId: string
  tags: SafetyQrTagDefinition[]
  vehicleClass: SafetyQrVehicleClass
}

export interface SafetyQrFamilyDefinition {
  approvalStatus: "approved"
  displayName: string
  notificationPolicyId: string
  productFamily: SafetyQrProductFamily
  variants: SafetyQrVariantDefinition[]
}

export interface SafetyQrCatalog {
  catalogId: "safetyqr_kit_packaging"
  catalogVersion: string
  lastReviewedOn: string
  openDecisions: SafetyQrOpenDecision[]
  productFamilies: SafetyQrFamilyDefinition[]
  sourceSpecPath: string
}

export const safetyQrCatalog = rawCatalog as SafetyQrCatalog

export function listSafetyQrFamilies() {
  return safetyQrCatalog.productFamilies
}

export function listSafetyQrVariants() {
  return listSafetyQrFamilies().flatMap((family) => family.variants)
}

export function listLaunchableSafetyQrVariants() {
  return listSafetyQrVariants().filter((variant) => variant.launchGate.launchable)
}

export function getSafetyQrVariant(kitVariant: SafetyQrKitVariant) {
  return listSafetyQrVariants().find((variant) => variant.kitVariant === kitVariant) ?? null
}

export function isSafetyQrKitVariant(
  kitVariant: string | null | undefined
): kitVariant is SafetyQrKitVariant {
  return Boolean(kitVariant && getSafetyQrVariant(kitVariant))
}

export function resolveSafetyQrTagCatalogEntry(input: {
  kitVariant?: string | null
  productFamily?: string | null
  tagRole?: string | null
  tagSlot?: string | null
}) {
  const variant = input.kitVariant ? getSafetyQrVariant(input.kitVariant) : null

  if (!variant) {
    return null
  }

  if (input.productFamily && variant.productFamily !== input.productFamily) {
    return null
  }

  const tagDefinition =
    variant.tags.find(
      (tag) =>
        (input.tagRole ? tag.role === input.tagRole : false) ||
        (input.tagSlot ? tag.slot === input.tagSlot : false)
    ) ?? null

  if (!tagDefinition) {
    return null
  }

  return {
    tagDefinition,
    variant,
  }
}
